import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { CampaignV2 } from 'shared/src/api/campaigns-v2';
import { CampaignIO } from 'shared/src/api/io-types';
import { trpc } from '@/utils/trpc-client';

type ContextValue = {
  campaign: CampaignV2;
  ios: CampaignIO[];
  iosLoading: boolean;
  iosError: boolean;
};

const CampaignContext = createContext<ContextValue | null>(null);

const EMPTY_IOS: CampaignIO[] = [];

type Props = {
  campaign: CampaignV2;
  children: ReactNode;
};

export function CampaignContextProvider({ campaign, children }: Props) {
  const iosQuery = trpc.campaignInsertionOrders.useQuery(
    { zoho_campaign_id: campaign.campaign_id },
    { refetchInterval: 60_000 }
  );

  const value = useMemo(
    () => ({
      campaign,
      ios: iosQuery.data ?? EMPTY_IOS,
      iosLoading: iosQuery.isLoading,
      iosError: iosQuery.isError
    }),
    [campaign, iosQuery.data, iosQuery.isLoading, iosQuery.isError]
  );

  return <CampaignContext value={value}>{children}</CampaignContext>;
}

export function useCampaignContext() {
  const value = useContext(CampaignContext);
  if (!value) {
    throw new Error('useCampaignContext must be used within a CampaignContextProvider');
  }
  return value;
}
